import React, { useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Input, Select, Table, TableProps } from 'antd';
import { EditTwoTone } from '@ant-design/icons';
import moment, { Moment } from 'moment';
import { numberWithCommas } from 'utils';
import { useExcelDownload, useGroupGiftOrders } from 'hooks';
import {
  ORDER_STATUS,
  TGroupGiftOrderStatus,
  IGroupGiftOrder,
  IGroupGiftOrdersParams,
  TGroupGiftDateType,
  TGroupGiftKeywordType,
  TGroupGiftTrackingStatus
} from 'interfaces/bizGift';
import { WhiteWrapper, TableWrapper } from 'pages/SalesSettlement/styles';
import { FilterBar, SummaryCardList, SummaryCard, TableTopRow, StatusText, OrderNoButton } from './styles';
import OrderDetailModal from './OrderDetailModal';
import DeliveryUploadModal from './DeliveryUploadModal';
import { addPhoneHyphen, formatDate } from './helpers';

const { RangePicker } = DatePicker;

const PAGE_SIZE = 50;

interface IFilter {
  dateType: TGroupGiftDateType;
  dateRange: [Moment, Moment];
  orderStatus: 'ALL' | TGroupGiftOrderStatus;
  isTracking: TGroupGiftTrackingStatus;
  keywordType: TGroupGiftKeywordType;
  keyword: string;
}

const DEFAULT_FILTER: IFilter = {
  dateType: 'REQUEST_SHIPMENT_DATE',
  dateRange: [moment().add(-7, 'day'), moment().add(7, 'day')],
  orderStatus: 'ALL',
  isTracking: 'UNREGISTERED',
  keywordType: 'ORDER_NO',
  keyword: ''
};

const BizgiftOrderManagement = () => {
  const [filter, setFilter] = useState<IFilter>(DEFAULT_FILTER);
  const [appliedFilter, setAppliedFilter] = useState<IFilter>(DEFAULT_FILTER);
  const [page, setPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState<IGroupGiftOrder | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const columns: TableProps<IGroupGiftOrder>['columns'] = useMemo(() => {
    const baseColumns: TableProps<IGroupGiftOrder>['columns'] = [
      {
        title: '주문번호',
        dataIndex: 'groupGiftOrderIdx',
        align: 'center',
        width: 110,
        render: (orderNo: string, record: IGroupGiftOrder) => (
          <OrderNoButton type="button" onClick={() => setSelectedOrder(record)}>
            {orderNo}
            <EditTwoTone />
          </OrderNoButton>
        )
      },
      { title: '회사명', dataIndex: 'companyName', align: 'center', width: 140 },
      { title: '상품코드', dataIndex: 'productCode', align: 'center', width: 100 },
      { title: '상품명', dataIndex: 'productName', align: 'center', width: 160 },
      { title: '상품옵션', dataIndex: 'productOption', align: 'center', width: 100 },
      {
        title: '공급금액',
        dataIndex: 'supplyPrice',
        align: 'center',
        width: 100,
        render: (value: number) => numberWithCommas(value)
      },
      {
        title: '상태',
        dataIndex: 'status',
        align: 'center',
        width: 100,
        render: (status: TGroupGiftOrderStatus) => (
          <StatusText $color={ORDER_STATUS[status].color}>{ORDER_STATUS[status].label}</StatusText>
        )
      },
      { title: '주문 확정일', dataIndex: 'confirmDate', align: 'center', width: 150 },
      { title: '출고 희망일', dataIndex: 'requestShipmentDate', align: 'center', width: 120 },
      { title: '택배사', dataIndex: 'logisticsName', align: 'center', width: 120, render: (value) => value || '-' },
      { title: '송장번호', dataIndex: 'trackingNumber', align: 'center', width: 140, render: (value) => value || '-' },
      { title: '보내는사람', dataIndex: 'senderName', align: 'center', width: 150 },
      { title: '보내는사람 도로명주소', dataIndex: 'senderRoadAddress', align: 'center', width: 150 },
      { title: '보내는사람 지번주소', dataIndex: 'senderJibunAddress', align: 'center', width: 150 },
      { title: '보내는사람 상세주소', dataIndex: 'senderAddressDetail', align: 'center', width: 150 },
      { title: '보내는사람 우편번호', dataIndex: 'senderZipCode', align: 'center', width: 150 },
      { title: '고객사 요청사항', dataIndex: 'companyRequirement', align: 'center', width: 150 },
      { title: '받는사람', dataIndex: 'receiverName', align: 'center', width: 100 },
      { title: '받는사람 전화번호', dataIndex: 'receiverPhone', align: 'center', width: 140 },
      { title: '받는사람 도로명주소', dataIndex: 'receiverRoadAddress', align: 'center', width: 150 },
      { title: '받는사람 지번주소', dataIndex: 'receiverJibunAddress', align: 'center', width: 150 },
      { title: '받는사람 상세주소', dataIndex: 'receiverAddressDetail', align: 'center', width: 150 },
      { title: '받는사람 우편번호', dataIndex: 'receiverZipCode', align: 'center', width: 150 },
      { title: '배송 요청사항', dataIndex: 'deliveryRequirement', align: 'center', width: 150 }
    ];
    return baseColumns.map((column) => ({ ellipsis: { showTitle: true }, ...column }));
  }, []);

  const changeFilter = (patch: Partial<IFilter>) => {
    if (patch.dateType === 'CONFIRM_DATE') {
      setFilter((prev) => ({ ...prev, ...patch, dateRange: [moment().add(-1, 'month'), moment()] }));
    } else if (patch.dateType === 'REQUEST_SHIPMENT_DATE') {
      setFilter((prev) => ({ ...prev, ...patch, dateRange: [moment().add(-7, 'day'), moment().add(7, 'day')] }));
    }
    setFilter((prev) => ({ ...prev, ...patch }));
  };

  const handleSearch = () => {
    setPage(1);
    setAppliedFilter(filter);
  };

  // 적용된 필터 + 페이지 → 목록 조회 API 파라미터
  const params = useMemo<IGroupGiftOrdersParams>(() => {
    const { dateType, dateRange, orderStatus, isTracking, keywordType, keyword } = appliedFilter;
    const [startDate, endDate] = dateRange;
    const normalizedKeyword = keyword.trim();

    return {
      page,
      size: PAGE_SIZE,
      dateType,
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      orderStatuses: orderStatus === 'ALL' ? undefined : [orderStatus],
      trackingStatus: isTracking,
      ...(normalizedKeyword ? { keywordType, keyword: normalizedKeyword } : {})
    };
  }, [appliedFilter, page]);

  const { data, isFetching } = useGroupGiftOrders(params);

  const { changeExcelParams } = useExcelDownload();
  useEffect(() => {
    const excelBody = { ...params };
    delete excelBody.page;
    delete excelBody.size;
    changeExcelParams({ data: excelBody });
  }, [params, changeExcelParams]);

  const orders = useMemo<IGroupGiftOrder[]>(() => {
    return (data?.content ?? []).map((order) => ({
      ...order,
      productOption: order.productOption ?? '-',
      status: order.status as TGroupGiftOrderStatus,
      receiverName: order.receiverName ?? '-',
      receiverPhone: addPhoneHyphen(order.receiverPhone),
      receiverZipCode: order.receiverZipCode ?? '-',
      receiverRoadAddress: order.receiverRoadAddress ?? '-',
      receiverJibunAddress: order.receiverJibunAddress ?? '-',
      receiverAddressDetail: order.receiverAddressDetail ?? '-',
      confirmDate: formatDate(order.confirmDate),
      requestShipmentDate: formatDate(order.requestShipmentDate),
      deliveryRequirement: order.deliveryRequirement ?? '-',
      logisticsName: order.logisticsName ?? '-',
      trackingNumber: order.trackingNumber ?? '-',
      companyRequirement: order.companyRequirement ?? '-',
      senderName: order.senderName ?? '-',
      senderRoadAddress: order.senderRoadAddress ?? '-',
      senderJibunAddress: order.senderJibunAddress ?? '-',
      senderAddressDetail: order.senderAddressDetail ?? '-',
      senderZipCode: order.senderZipCode ?? '-'
    }));
  }, [data]);

  const summary = {
    total: data?.totalCount ?? 0,
    confirmed: data?.confirmedCount ?? 0,
    delivering: data?.deliveringCount ?? 0,
    delivered: data?.deliveredCount ?? 0
  };

  const handleUploadClick = () => {
    setUploadModalOpen(true);
  };

  return (
    <div>
      <FilterBar>
        <span className="filter-label">기간 검색</span>
        <Select value={filter.dateType} style={{ width: 140 }} onChange={(value) => changeFilter({ dateType: value })}>
          <Select.Option value="REQUEST_SHIPMENT_DATE">출고 희망일</Select.Option>
          <Select.Option value="CONFIRM_DATE">주문 확정일</Select.Option>
        </Select>
        <RangePicker
          value={filter.dateRange}
          allowClear={false}
          onChange={(dates) => dates && changeFilter({ dateRange: dates as [Moment, Moment] })}
        />
        <span className="filter-label">주문 상태</span>
        <Select value={filter.orderStatus} style={{ width: 140 }} onChange={(value) => changeFilter({ orderStatus: value })}>
          <Select.Option value="ALL">전체</Select.Option>
          <Select.Option value="CONFIRMED">주문 확정</Select.Option>
          <Select.Option value="DELIVERING">배송중</Select.Option>
          <Select.Option value="DELIVERED">배송완료</Select.Option>
        </Select>
        <span className="filter-label">송장 등록 여부</span>
        <Select value={filter.isTracking} style={{ width: 140 }} onChange={(value) => changeFilter({ isTracking: value })}>
          <Select.Option value="ALL">전체</Select.Option>
          <Select.Option value="UNREGISTERED">미등록</Select.Option>
          <Select.Option value="REGISTERED">등록</Select.Option>
        </Select>
        <span className="filter-label">키워드 검색</span>
        <Select value={filter.keywordType} style={{ width: 120 }} onChange={(value) => changeFilter({ keywordType: value })}>
          <Select.Option value="ORDER_NO">주문번호</Select.Option>
          <Select.Option value="RECEIVER_NAME">받는사람</Select.Option>
          <Select.Option value="TRACKING_NUMBER">송장번호</Select.Option>
        </Select>
        <Input
          style={{ width: 200, height: 40 }}
          placeholder="전체"
          maxLength={50}
          value={filter.keyword}
          onChange={({ target }) => changeFilter({ keyword: target.value })}
          onPressEnter={handleSearch}
        />
        <Button onClick={handleSearch}>검색</Button>
      </FilterBar>

      <SummaryCardList>
        <SummaryCard>
          <div className="summary-label">전체</div>
          <div className="summary-count">{summary.total}건</div>
        </SummaryCard>
        <SummaryCard $countColor="#ff9f0a">
          <div className="summary-label">주문확정</div>
          <div className="summary-count">{summary.confirmed}건</div>
        </SummaryCard>
        <SummaryCard $countColor="#46b476">
          <div className="summary-label">배송중</div>
          <div className="summary-count">{summary.delivering}건</div>
        </SummaryCard>
        <SummaryCard>
          <div className="summary-label">배송완료</div>
          <div className="summary-count">{summary.delivered}건</div>
        </SummaryCard>
      </SummaryCardList>

      <WhiteWrapper>
        <TableTopRow>
          <span className="table-notice">
            송장번호와 택배사를 저장하면 <strong>배송중</strong> 상태로 변경됩니다. 배송정보 업로드로 여러 건을 한 번에 등록할 수
            있습니다.
          </span>
          <Button type="primary" onClick={handleUploadClick}>
            배송 정보 업로드
          </Button>
        </TableTopRow>
        <TableWrapper>
          <Table
            rowKey="orderNo"
            columns={columns}
            dataSource={orders}
            loading={isFetching}
            scroll={{ x: 2200 }}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total: summary.total,
              showSizeChanger: false,
              onChange: setPage
            }}
          />
        </TableWrapper>
      </WhiteWrapper>
      <OrderDetailModal groupGiftOrderIdx={selectedOrder?.groupGiftOrderIdx ?? null} onClose={() => setSelectedOrder(null)} />
      <DeliveryUploadModal open={uploadModalOpen} onClose={() => setUploadModalOpen(false)} />
    </div>
  );
};

export default BizgiftOrderManagement;
